import { LOCATION_CHANGE } from 'redux-first-history';
import { all, getContext, select, take, takeEvery } from 'redux-saga/effects';
import type { RootState } from 'src/store';
import type { RemoveTabAction } from 'src/store/actions/tab';
import type { SagaContext } from 'src/store/types';

/**
 * Watches for changes in the active tab by monitoring state changes
 */
function* watchTabFocusChanges() {
    try {
        const container: SagaContext['container'] = yield getContext('container');
        const analyticsService = container.getOptional('AnalyticsService');
        const logger = container.get('Logger').getSubLogger({ name: 'analytics-saga' });

        let lastActiveTabId: string | null = null;

        while (true) {
            try {
                // Wait for any action
                yield take('*');

                // Get the current state after the action was processed
                const state: RootState = yield select();

                // Get the current active tab (if any)
                const activeContainerId = state.container?.activeContainerId;
                const currentContainer = activeContainerId ? state.container.byId[activeContainerId] : null;
                const currentActiveTabId = currentContainer?.activeTabId ?? null;

                // Skip if there's no active tab or it hasn't changed
                if (!currentActiveTabId || currentActiveTabId === lastActiveTabId) {
                    continue;
                }

                // Get the tab data
                const tab = state.tab.byId[currentActiveTabId];
                if (!tab?.widget) {
                    continue;
                }

                // Track the widget focus event
                const currentWorkspace = state.workspace.currentWorkspace;
                analyticsService?.track({
                    type: 'widget_focused',
                    dashboardId: currentWorkspace?.id || '',
                    widgetType: tab.widget.type,
                    widgetId: tab.id,
                });

                // Update tracking variable
                lastActiveTabId = currentActiveTabId;
            } catch (innerError) {
                logger.error('Error in tab focus tracking loop', {
                    error: innerError instanceof Error ? innerError : String(innerError),
                });
            }
        }
    } catch (error) {
        const container: SagaContext['container'] = yield getContext('container');
        const logger = container.get('Logger').getSubLogger({ name: 'analytics-saga' });

        logger.error('Fatal error in widget focus tracking saga', {
            error: error instanceof Error ? error : String(error),
        });
        throw error;
    }
}

/**
 * Watches for widget removals (removeTab actions) and tracks them
 */
function* watchWidgetRemovals() {
    const container: SagaContext['container'] = yield getContext('container');
    const analyticsService = container.getOptional('AnalyticsService');
    const logger = container.get('Logger').getSubLogger({ name: 'analytics-saga' });

    yield takeEvery('removeTab', function* (action: RemoveTabAction) {
        try {
            const state: RootState = yield select();
            const currentWorkspace = state.workspace.currentWorkspace;

            analyticsService?.track({
                type: 'widget_removed',
                dashboardId: currentWorkspace?.id || '',
                widgetType: action.widgetType,
                widgetId: action.tabId,
            });
        } catch (error) {
            logger.error('Error tracking widget removal', {
                error: error instanceof Error ? error : String(error),
            });
        }
    });
}

/**
 * Normalizes a pathname by stripping dynamic IDs/slugs for analytics.
 * e.g. /dashboard/abc-123/my-workspace → /dashboard
 */
export function normalizeRoute(pathname: string): string {
    if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return '/dashboard';
    if (pathname === '/admin' || pathname.startsWith('/admin/')) return '/admin';
    if (pathname === '/callback' || pathname.startsWith('/callback/') || pathname.startsWith('/callback?')) {
        return '/callback';
    }
    return pathname;
}

/**
 * Watches for route changes and tracks page_viewed events
 */
function* watchRouteChanges() {
    const container: SagaContext['container'] = yield getContext('container');
    const analyticsService = container.getOptional('AnalyticsService');
    const logger = container.get('Logger').getSubLogger({ name: 'analytics-saga' });

    let lastPath: string | null = null;

    yield takeEvery(LOCATION_CHANGE, function* () {
        try {
            const state: RootState = yield select();
            const currentPath = state.router.location?.pathname;

            if (!currentPath || currentPath === lastPath) return;

            lastPath = currentPath;
            const route = normalizeRoute(currentPath);

            analyticsService?.track({
                type: 'page_viewed',
                pathname: currentPath,
                route,
            });
        } catch (error) {
            logger.error('Error tracking route change', {
                error: error instanceof Error ? error : String(error),
            });
        }
    });
}

/**
 * Saga that handles analytics tracking
 */
export function* analyticsSagas() {
    yield all([watchTabFocusChanges(), watchWidgetRemovals(), watchRouteChanges()]);
}
